import {
  bytesToHex,
  compareUtf8Bytes,
  concatBytes,
  getSubtleCrypto,
  lengthPrefixedUtf8,
  sha256,
  u8,
  u32be,
  utf8
} from "./encoding";
import { fail } from "./errors";
import type { NormalizedDealConfig, SeatDeal } from "./types";

const FINGERPRINT_MAGIC = utf8("YSI-DEAL-FINGERPRINT-V1\0");

function encodeSeatRecord(deal: SeatDeal): Uint8Array {
  const initial = [...deal.initial].sort(compareUtf8Bytes);
  return concatBytes(
    u8(deal.seat),
    lengthPrefixedUtf8(deal.packetId),
    u32be(initial.length),
    ...initial.map(lengthPrefixedUtf8),
    u32be(deal.penalty.length),
    ...deal.penalty.map(lengthPrefixedUtf8)
  );
}

export function encodeFingerprint(
  config: NormalizedDealConfig,
  themeId: string,
  themeRevision: string,
  lineupId: string,
  deals: readonly SeatDeal[]
): Uint8Array {
  if (deals.length !== config.playerCount) {
    fail("INVALID_CATALOG", "Fingerprint deal count differs from playerCount");
  }
  for (const [index, deal] of deals.entries()) {
    if (deal.seat !== index + 1) {
      fail("INVALID_CATALOG", "Fingerprint deals must be in contiguous seat order");
    }
  }

  return concatBytes(
    FINGERPRINT_MAGIC,
    lengthPrefixedUtf8(config.rulesVersion),
    lengthPrefixedUtf8(config.dealVersion),
    lengthPrefixedUtf8(config.matcherVersion),
    lengthPrefixedUtf8(config.catalogRevision),
    lengthPrefixedUtf8(config.normalizedSeed),
    u8(config.playerCount),
    u32be(config.round),
    lengthPrefixedUtf8(themeId),
    lengthPrefixedUtf8(themeRevision),
    lengthPrefixedUtf8(lineupId),
    u32be(config.playerCount),
    ...deals.map(encodeSeatRecord)
  );
}

export async function computeDealFingerprint(
  config: NormalizedDealConfig,
  themeId: string,
  themeRevision: string,
  lineupId: string,
  deals: readonly SeatDeal[],
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<string> {
  const digest = await sha256(
    encodeFingerprint(config, themeId, themeRevision, lineupId, deals),
    subtle
  );
  return `sha256:${bytesToHex(digest)}`;
}
